import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Activity, Search, Shield, Clock, User, ShieldAlert } from 'lucide-react';

export const AdminActivityLogsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: logsRes, isLoading } = useQuery({
    queryKey: ['admin-activity-logs'],
    queryFn: () => api.get('/admin/activity-logs').then((res) => res.data.data),
  });

  const logs = logsRes || [];
  const filteredLogs = logs.filter((l: any) => {
    const q = searchTerm.toLowerCase();
    return (
      l.action.toLowerCase().includes(q) ||
      l.entityType.toLowerCase().includes(q) ||
      l.user?.email?.toLowerCase().includes(q) ||
      l.user?.firstName?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              SECURITY & AUDIT
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <Activity className="w-7 h-7 text-[#FF6B4A]" />
            <span>System Activity &amp; Audit Trail</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Immutable security log recording logins, document downloads, project status shifts, and administrative modifications.
          </p>
        </div>

        <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono font-bold text-slate-700 shrink-0">
          <Shield className="w-4 h-4 text-emerald-600" />
          <span>Total Records: <strong className="text-slate-900">{logs.length}</strong></span>
        </div>
      </div>

      {/* 2. Search & Filter Bar */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-96">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search action, user, email, or entity..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 placeholder-slate-400 focus:outline-hidden focus:bg-white focus:border-brand-500 transition-colors"
          />
        </div>
        <span className="text-xs font-mono text-slate-500">
          Showing <strong>{filteredLogs.length}</strong> of {logs.length} log events
        </span>
      </div>

      {/* 3. Logs Table */}
      {isLoading ? (
        <div className="p-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">
          Loading audit trail...
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="p-16 rounded-2xl bg-white border border-slate-200 text-center space-y-3 shadow-sm">
          <Activity className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-900">No Audit Records Found</h3>
          <p className="text-xs text-slate-500">No log entries matched your filter parameters.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl md:rounded-3xl border border-slate-200/90 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700">
              <thead className="bg-slate-50/80 text-slate-500 font-mono text-[11px] uppercase border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 font-bold">Timestamp</th>
                  <th className="px-6 py-4 font-bold">Action</th>
                  <th className="px-6 py-4 font-bold">Entity</th>
                  <th className="px-6 py-4 font-bold">User / Actor</th>
                  <th className="px-6 py-4 font-bold">IP &amp; Payload Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono text-[11px]">
                {filteredLogs.map((log: any) => (
                  <tr key={log.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-4 text-slate-500 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 font-bold">
                      <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-800 border border-slate-200 font-mono text-[10px]">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-900 font-bold">
                      {log.entityType} {log.entityId ? <span className="text-slate-400 font-normal">[{log.entityId}]</span> : ''}
                    </td>
                    <td className="px-6 py-4 text-slate-800 font-sans">
                      {log.user ? (
                        <div>
                          <div className="font-bold text-slate-900">{log.user.firstName} {log.user.lastName}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{log.user.email} &bull; {log.user.role}</div>
                        </div>
                      ) : (
                        <span className="text-slate-500 font-mono">System / Guest</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-500 max-w-xs truncate font-mono">
                      {log.details ? JSON.stringify(log.details) : log.ipAddress || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};
